import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Typography,
  IconButton,
  DialogContent,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import moment from "moment";
import toast from "react-hot-toast";
import InternalAdminForm from "../InternalAdmins/InternalAdminForm";
import EditIcon from "@mui/icons-material/Edit";
import { useMutation } from "@apollo/client";
import {
  USER_MASJID_ACTION,
  SWITCH_ROLE,
  CHANGE_EMAIL,
  DELETE_USER,
  UPDATE_USER_BIOMETRIC,
} from "../../../graphql/Admins/mutation";
import { ApolloError } from "@apollo/client";

import { AdminStatus, getAdminStatus } from "../helpers";
import { roleRenamer } from "../../../helpers/RoleRenamer";
import ReasonConfirmation from "./ReasonConfirmation";
import DeleteConfirmation from "./DeleteConfirmation";
import { ReSendingReinvitation } from "../../../Redux/Actions/AuthActions/ResendingInvitation";
import EmailFilledIcon from "../../../Photos/Newuiphotos/Admins/EmailFilledIcon.png";
import StatusLogs from "../StatusLogs/StatusLogs";
import { useAppThunkDispatch, useAppSelector } from "../../../Redux/hooks";
import { ChangeSnackbar } from "../../../Redux/Actions/SnackbarActions/SnackbarActions";
import {
  getExternalAdminRoleLabel,
  isDelegatedSubAdmin,
} from "../../../helpers/rbacExternalAdmin";
import {
  getAccessToken,
  redirectToVanillaPortalSettings,
} from "../../../helpers/HelperFunction";

const ValueFieldStyles = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  color: "#1B8368",
  fontWeight: "bold",
};

const getValueFieldStyles = (value: any) => {
  if (value) {
    return ValueFieldStyles;
  } else {
    return { ...ValueFieldStyles, color: "#FF4949" };
  }
};

interface ExternalAdminHandler2Props {
  cooldowns: { [key: string]: number };
  setCooldowns: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>;
  isLoading: boolean;
  showReasonConfirmation: boolean;
  setShowReasonConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  handleUpdateAdminStatus: (status: string) => void;
  adminStatus: string;
  reason: string;
  setReason: React.Dispatch<React.SetStateAction<string>>;
  reasonError: boolean;
  setReasonError: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAdmin: any;
  showLogs: boolean;
  setShowLogs: React.Dispatch<React.SetStateAction<boolean>>;
  logs: any;
  refetch: () => void;
  setOpen: (open: boolean) => void;
  setSkipfetchAdmin: React.Dispatch<React.SetStateAction<boolean>>;
}

const ExternalAdminHandler2 = ({
  cooldowns,
  setCooldowns,
  isLoading,
  showReasonConfirmation,
  setShowReasonConfirmation,
  handleUpdateAdminStatus,
  adminStatus,
  reason,
  setReason,
  reasonError,
  setReasonError,
  selectedAdmin,
  showLogs,
  setShowLogs,
  logs,
  refetch,
  setOpen,
  setSkipfetchAdmin,
}: ExternalAdminHandler2Props) => {
  const dispatch = useAppThunkDispatch();
  const [freezeModalOpen, setFreezeModalOpen] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [sendInvitationModal, setSendInvitationModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [updateAdminMasjid] = useMutation(USER_MASJID_ACTION);
  const [switchRole] = useMutation(SWITCH_ROLE);
  const [changeEmail] = useMutation(CHANGE_EMAIL);
  const [deleteUser] = useMutation(DELETE_USER);
  const [updateUserBiometric] = useMutation(UPDATE_USER_BIOMETRIC);
  const getCooldownForAdmin = (adminId: string) => cooldowns[adminId] || 0;

  const handleOpenRbacPortal = () => {
    const masjidId = selectedAdmin?.masjidDetails?.[0]?._id as string | undefined;
    if (!masjidId) {
      toast.error("Masjid details are required to open the portal.");
      return;
    }
    const baseUrl = import.meta.env.VITE_VANILLA_URL;
    if (!getAccessToken() || !baseUrl) {
      toast.error("Unable to open portal. Please sign in again.");
      return;
    }
    redirectToVanillaPortalSettings(masjidId, "MASJID", "user-management", {
      userId: selectedAdmin?._id,
      userEmail: selectedAdmin?.email,
    });
  };

  const handleCopy = (text: string | undefined) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy!");
      });
  };

  const handleAdminUpdate = async (
    name: string,
    email: string,
    contact: string,
    role: string,
    selectedMasjid: string,
    isEdit?: boolean
  ) => {
    if (!isEdit) return;

    const toastId = toast.loading("Updating Admin Details...");
    let hasChanges = false;
    let biometricUpdateSuccess = true;

    // Check if admin is associated with organization or individual (these skip role/masjid updates)
    const isOrganizationOrIndividual =
      selectedAdmin?.ownershipType === "ORGANIZATION" ||
      selectedAdmin?.ownershipType === "INDIVIDUAL";

    // Check if admin is associated with organization, individual, or masjid (these allow name/contact updates)
    const isOrganizationOrIndividualOrMasjid =
      selectedAdmin?.ownershipType === "ORGANIZATION" ||
      selectedAdmin?.ownershipType === "INDIVIDUAL" ||
      selectedAdmin?.ownershipType === "MASJID";

    try {
      // Check if email has changed
      if (email !== selectedAdmin?.email) {
        hasChanges = true;
        const { data: emailData, errors: emailErrors } = await changeEmail({
          variables: {
            input: {
              userId: selectedAdmin._id,
              newEmail: email,
            },
          },
        });

        if (emailErrors && emailErrors.length > 0) {
          throw new ApolloError({ graphQLErrors: emailErrors });
        }

        if (emailData?.changeEmail?.status !== "email_changed") {
          throw new Error("Failed to update email");
        }
      }

      // For organization/individual/masjid admins, check if name or contact has changed
      if (isOrganizationOrIndividualOrMasjid) {
        if (name !== selectedAdmin?.name || contact !== selectedAdmin?.contact) {
          // Validate that we have a valid userId
          if (!selectedAdmin?._id) {
            throw new Error("Invalid admin ID");
          }
          
          hasChanges = true;
          console.log("Selected Admin:", selectedAdmin);
          console.log("Updating biometric details for user:", selectedAdmin._id, "name:", name, "contact:", contact);
          
          // Only send fields that have actually changed
          const input: any = {
            userId: selectedAdmin._id,
          };
          
          if (name !== selectedAdmin?.name) {
            input.name = name;
          }
          if (contact !== selectedAdmin?.contact) {
            input.phone = contact;
          }
          
          // Also try sending email if it exists (some backends expect this)
          if (email) {
            input.email = email;
          }
          
          console.log("Biometric input:", input);
          
          try {
            const { data: biometricData, errors: biometricErrors } = await updateUserBiometric({
              variables: {
                input: input,
              },
            });

            if (biometricErrors && biometricErrors.length > 0) {
              throw new ApolloError({ graphQLErrors: biometricErrors });
            }

            if (biometricData?.updateUserBiometricDetails?.status !== "success") {
              throw new Error("Failed to update user biometric details");
            }
            
            console.log("Biometric update successful:", biometricData);
            biometricUpdateSuccess = true;
          } catch (biometricError) {
            console.error("Biometric update failed:", biometricError);
            biometricUpdateSuccess = false;
            // Don't show error toast here - we'll handle it in the success message
          }
        }
      }

      // Only update masjid and role if NOT organization/individual admin
      if (!isOrganizationOrIndividual) {
        // Check if masjid has changed
        if (
          selectedMasjid !== selectedAdmin?.masjidDetails?.[0]?._id &&
          !(selectedMasjid === "" && !selectedAdmin?.masjidDetails?.[0]?._id)
        ) {
          hasChanges = true;
          const variables = {
            userId: selectedAdmin._id,
            masjidId: selectedMasjid || selectedAdmin?.masjidDetails?.[0]?._id, // Empty string for unassign
            action: selectedMasjid ? "assign" : "unassign",
          };

          const { data, errors } = await updateAdminMasjid({
            variables,
          });

          if (errors && errors.length > 0) {
            throw new ApolloError({ graphQLErrors: errors });
          }

          if (!data?.userMasjidAction) {
            throw new Error(
              "No userMasjidAction was returned from the server."
            );
          }
        }

        // Check if role has changed
        if (role && role !== selectedAdmin?.role) {
          hasChanges = true;
          const { data: roleData, errors: roleErrors } = await switchRole({
            variables: {
              input: {
                userId: selectedAdmin._id,
                role: role,
              },
            },
          });

          if (roleErrors && roleErrors.length > 0) {
            throw new ApolloError({ graphQLErrors: roleErrors });
          }

          if (roleData?.switchRole.status !== "role_changed") {
            throw new Error("No switchRole was returned from the server.");
          }
        }
      }

      if (hasChanges) {
        toast.dismiss(toastId);
        
        // Show appropriate success message based on what actually worked
        if (isOrganizationOrIndividualOrMasjid && (name !== selectedAdmin?.name || contact !== selectedAdmin?.contact)) {
          if (biometricUpdateSuccess) {
            toast.success("Admin Details Updated Successfully");
          } else {
            toast.success("Email updated successfully, but name/contact update failed");
          }
        } else {
          toast.success("Admin Details Updated Successfully");
        }
        
        setSkipfetchAdmin(false);
        refetch();
      } else {
        toast.dismiss(toastId);
        toast.success("No changes to update");
      }
    } catch (error) {
      toast.dismiss(toastId);
      let errorMessage = "Error Updating Admin Details";
      if (error instanceof ApolloError && error.graphQLErrors.length) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setShowEditForm(false);
    }
  };

  const handleDeleteAdmin = async (reason: string, description: string) => {
    if (!selectedAdmin) return;

    setDeleteLoading(true);
    try {
      await deleteUser({
        variables: {
          input: {
            userId: selectedAdmin._id,
            reason: reason,
            description: description,
            metadata: JSON.stringify({
              adminName: selectedAdmin.name,
              adminEmail: selectedAdmin.email,
              deletedFrom: "ExternalAdminPanel",
            }),
          },
        },
      });

      toast.success("Admin deleted successfully and moved to archives");
      setShowDeleteConfirmation(false);
      setSkipfetchAdmin(false);
      refetch();
      setOpen(false); // Close the admin details dialog
    } catch (error) {
      console.error("Delete admin error:", error);
      let errorMessage = "Error deleting admin";
      if (error instanceof ApolloError && error.graphQLErrors.length) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSendInvitation = () => {
    if (selectedAdmin?.email) {
      toast.loading("Sending Invitation...");
      const response = dispatch(ReSendingReinvitation(selectedAdmin.email));
      response.then(function (result: any) {
        if (result.success) {
          toast.dismiss();
          dispatch(
            ChangeSnackbar({
              snackbarOpen: true,
              snackbarType: "success",
              snackbarMessage: "Sent the email successfully",
            })
          );
          setSendInvitationModal(false);
          setCooldowns((prev) => {
            const newCooldowns = { ...prev };
            newCooldowns[selectedAdmin._id] = 30; // 30 sec cooldown
            return newCooldowns;
          });
        } else {
          toast.dismiss();
          dispatch(
            ChangeSnackbar({
              snackbarOpen: true,
              snackbarType: "error",
              snackbarMessage: "Failed to send the email",
            })
          );
          setSendInvitationModal(false);
        }
      });
    }
  };

  return (
    <div
      style={{
        display: "flex",
        height: "100%",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Dialog
        open={sendInvitationModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Are you sure you want to send an invitation again?"}
        </DialogTitle>
        <DialogActions style={{ justifyContent: "space-around" }}>
          <Button
            onClick={() => {
              setSendInvitationModal(false);
            }}
            variant="outlined"
          >
            No
          </Button>
          <Button onClick={handleSendInvitation} variant="outlined">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {isLoading ? (
        <CircularProgress />
      ) : showReasonConfirmation ? (
        <ReasonConfirmation
          onConfirm={(status: string) => {
            if (!reason) {
              setReasonError(true);
              return;
            }
            handleUpdateAdminStatus(status);
          }}
          reason={reason}
          setReason={setReason}
          reasonError={reasonError}
          setReasonError={setReasonError}
          isLoading={isLoading}
          status={
            adminStatus === AdminStatus.DEACTIVE ? "approving" : "freezing"
          }
        />
      ) : showDeleteConfirmation ? (
        <DeleteConfirmation
          open={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleDeleteAdmin}
          adminName={selectedAdmin?.name}
          loading={deleteLoading}
        />
      ) : showLogs ? (
        <StatusLogs logs={logs} feature={""} />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            marginTop: "12px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flex: "auto",
          }}
        >
          <Box>
            {/* ---------- Status Section if Rejected/Freezed ---------- */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                {getAdminStatus(
                  selectedAdmin?.isFreezed,
                  selectedAdmin?.isVerified
                ) === "rejected" && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Status
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={getValueFieldStyles("")}>
                        Freezed
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy("Freezed")}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </>
                )}
              </div>
              {adminStatus !== AdminStatus.DEACTIVE && (
                <Button
                  variant="contained"
                  sx={{
                    width: "105px",
                    borderRadius: "8px",
                    bgcolor: "#1B8368",
                    "&:hover": { bgcolor: "#1B8368" },
                    textTransform: "none",
                    padding: "2.5px",
                  }}
                  onClick={() => {
                    setShowEditForm(true);
                  }}
                >
                  Edit
                  <EditIcon sx={{ width: "20px", marginLeft: "5px" }} />
                </Button>
              )}
            </Box>

            {isDelegatedSubAdmin(selectedAdmin) ? (
              <Box sx={{ width: "100%", mt: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: "#1B8368",
                    color: "#1B8368",
                    textTransform: "none",
                    borderRadius: "10px",
                    "&:hover": {
                      borderColor: "#146652",
                      bgcolor: "rgba(27, 131, 104, 0.06)",
                    },
                  }}
                  onClick={handleOpenRbacPortal}
                >
                  Manage access in entity portal
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.75,
                    color: "text.secondary",
                    textAlign: "center",
                  }}
                >
                  Opens User Management in the admin portal for this masjid.
                </Typography>
              </Box>
            ) : null}

            {/* ---------- Role ---------- */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Role
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={getValueFieldStyles(selectedAdmin?.role)}
                  >
                    {getExternalAdminRoleLabel(selectedAdmin)}
                  </Typography>
                  {selectedAdmin?.role && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCopy(getExternalAdminRoleLabel(selectedAdmin))
                      }
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>

            {/* ---------- Entity Type ---------- */}
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Entity Type
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                sx={getValueFieldStyles(selectedAdmin?.ownershipType)}
              >
                {selectedAdmin?.ownershipType
                  ? selectedAdmin.ownershipType.charAt(0).toUpperCase() +
                    selectedAdmin.ownershipType.slice(1).toLowerCase()
                  : "Not Available"}
              </Typography>
              {selectedAdmin?.ownershipType && (
                <IconButton
                  size="small"
                  onClick={() => handleCopy(selectedAdmin.ownershipType)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* ---------- Entity (for non-admin/superadmin) ---------- */}
            {selectedAdmin?.role !== "admin" &&
              selectedAdmin?.role !== "superadmin" && (
                <>
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Assigned Entity
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...getValueFieldStyles(
                            (() => {
                              if (
                                selectedAdmin?.ownershipType === "ORGANIZATION"
                              ) {
                                const hasOrg =
                                  selectedAdmin?.organizationDetails &&
                                  selectedAdmin.organizationDetails.length > 0;
                                return hasOrg
                                  ? selectedAdmin.organizationDetails[0]?.name
                                  : "";
                              } else if (
                                selectedAdmin?.ownershipType === "INDIVIDUAL"
                              ) {
                                const hasInd =
                                  selectedAdmin?.individualDetails &&
                                  selectedAdmin.individualDetails.length > 0;
                                return hasInd
                                  ? selectedAdmin.individualDetails[0]?.name
                                  : "";
                              } else {
                                const hasMasjid =
                                  selectedAdmin?.masjidDetails &&
                                  selectedAdmin.masjidDetails.length > 0;
                                return hasMasjid
                                  ? selectedAdmin.masjidDetails[0]?.masjidName
                                  : "";
                              }
                            })()
                          ),
                          ...(selectedAdmin?.masjidDetails &&
                            selectedAdmin.masjidDetails.length > 0 &&
                            !selectedAdmin?.isApproved &&
                            !selectedAdmin.isAddedFromAdmin && {
                              color: "#F7A400",
                            }),
                        }}
                      >
                        {(() => {
                          if (selectedAdmin?.ownershipType === "ORGANIZATION") {
                            const hasOrg =
                              selectedAdmin?.organizationDetails &&
                              selectedAdmin.organizationDetails.length > 0;
                            if (!hasOrg) return "Not Assigned";
                            const name =
                              selectedAdmin.organizationDetails[0]?.name;
                            return !selectedAdmin.isApproved &&
                              !selectedAdmin.isAddedFromAdmin
                              ? `Pending Assignment (${name})`
                              : name;
                          } else if (
                            selectedAdmin?.ownershipType === "INDIVIDUAL"
                          ) {
                            const hasInd =
                              selectedAdmin?.individualDetails &&
                              selectedAdmin.individualDetails.length > 0;
                            if (!hasInd) return "Not Assigned";
                            const name =
                              selectedAdmin.individualDetails[0]?.name;
                            return !selectedAdmin.isApproved &&
                              !selectedAdmin.isAddedFromAdmin
                              ? `Pending Assignment (${name})`
                              : name;
                          } else {
                            const hasMasjid =
                              selectedAdmin?.masjidDetails &&
                              selectedAdmin.masjidDetails.length > 0;
                            if (!hasMasjid) return "Not Assigned";
                            const name =
                              selectedAdmin.masjidDetails[0]?.masjidName;
                            return !selectedAdmin.isApproved &&
                              !selectedAdmin.isAddedFromAdmin
                              ? `Pending Assignment (${name})`
                              : name;
                          }
                        })()}
                      </Typography>

                      {(() => {
                        let name: string | null = null;
                        if (selectedAdmin?.ownershipType === "ORGANIZATION") {
                          const hasOrg =
                            selectedAdmin?.organizationDetails &&
                            selectedAdmin.organizationDetails.length > 0;
                          name = hasOrg
                            ? selectedAdmin.organizationDetails[0]?.name
                            : null;
                        } else if (
                          selectedAdmin?.ownershipType === "INDIVIDUAL"
                        ) {
                          const hasInd =
                            selectedAdmin?.individualDetails &&
                            selectedAdmin.individualDetails.length > 0;
                          name = hasInd
                            ? selectedAdmin.individualDetails[0]?.name
                            : null;
                        } else {
                          const hasMasjid =
                            selectedAdmin?.masjidDetails &&
                            selectedAdmin.masjidDetails.length > 0;
                          name = hasMasjid
                            ? selectedAdmin.masjidDetails[0]?.masjidName
                            : null;
                        }
                        return name ? (
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(name!)}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        ) : null;
                      })()}
                    </Box>
                  </>
                </>
              )}

            {/* ---------- Full Name ---------- */}
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Full Name
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                sx={getValueFieldStyles(selectedAdmin?.name)}
              >
                {selectedAdmin?.name ?? ""}
              </Typography>
              {selectedAdmin?.name && (
                <IconButton
                  size="small"
                  onClick={() => handleCopy(selectedAdmin?.name)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* ---------- Email Address ---------- */}
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Email Address
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                sx={getValueFieldStyles(selectedAdmin?.email)}
              >
                {selectedAdmin?.email ?? ""}
              </Typography>
              {selectedAdmin?.email && (
                <IconButton
                  size="small"
                  onClick={() => handleCopy(selectedAdmin?.email)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* ---------- Created/Updated Timestamps ---------- */}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box sx={{ width: "50%" }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, textTransform: "capitalize" }}
                >
                  Created At
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={getValueFieldStyles(selectedAdmin?.createdAt)}
                  >
                    {selectedAdmin?.createdAt
                      ? moment
                          .utc(selectedAdmin.createdAt)
                          .local()
                          .format("DD MMM, YYYY | hh:mm A")
                      : "Not Available"}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ width: "50%" }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, textTransform: "capitalize" }}
                >
                  Last Updated At
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={getValueFieldStyles(selectedAdmin?.updatedAt)}
                  >
                    {selectedAdmin?.updatedAt
                      ? moment
                          .utc(selectedAdmin.updatedAt)
                          .local()
                          .format("DD MMM, YYYY | hh:mm A")
                      : "Not Available"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ---------- Action Buttons & Logs ---------- */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "12px",
              mt: 3,
              gap: 2,
            }}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                gap: "20px",
              }}
            >
              {adminStatus === AdminStatus.ACTIVE ||
              adminStatus === AdminStatus.UNVERIFIED ? (
                <Button
                  variant="contained"
                  sx={{
                    width: "200px",
                    borderRadius: "20px",
                    bgcolor: "#FF2D30",
                    "&:hover": { bgcolor: "#FF2D30" },
                    textTransform: "none",
                  }}
                  onClick={() => {
                    setShowReasonConfirmation(true);
                  }}
                >
                  Freeze Admin
                </Button>
              ) : (
                <Button
                  variant="contained"
                  sx={{
                    width: "200px",
                    borderRadius: "20px",
                    bgcolor: "#00D23B",
                    "&:hover": { bgcolor: "#00D23B" },
                    textTransform: "none",
                  }}
                  onClick={() => {
                    setShowReasonConfirmation(true);
                  }}
                >
                  Unfreeze Admin
                </Button>
              )}
              {selectedAdmin &&
                !selectedAdmin.isVerified &&
                adminStatus !== AdminStatus.DEACTIVE && (
                  <Button
                    variant="contained"
                    sx={{
                      width: "200px",
                      borderRadius: "20px",
                      bgcolor: "#1B8368",
                      "&:hover": { bgcolor: "#1B8368" },
                      textTransform: "none",
                      "&.Mui-disabled": {
                        cursor: "not-allowed !important",
                        color: "#686868",
                      },
                    }}
                    onClick={() => {
                      setSendInvitationModal(true);
                    }}
                    disabled={getCooldownForAdmin(selectedAdmin?._id) > 0}
                  >
                    <Box
                      component={"img"}
                      src={EmailFilledIcon}
                      sx={{ width: "20px", marginRight: "5px" }}
                    />
                    Send Invitation{" "}
                    {getCooldownForAdmin(selectedAdmin?._id) > 0 &&
                      `(${getCooldownForAdmin(selectedAdmin?._id)}s)`}
                  </Button>
                )}
              <Button
                variant="contained"
                sx={{
                  width: "200px",
                  borderRadius: "20px",
                  bgcolor: "#FF2D30",
                  "&:hover": { bgcolor: "#FF2D30" },
                  textTransform: "none",
                }}
                onClick={() => {
                  setShowDeleteConfirmation(true);
                }}
              >
                Delete Admin
              </Button>
            </div>
            
            <Button
              variant="text"
              sx={{
                color: "#3D5347",
                textDecoration: "underline",
                textTransform: "none",
                fontSize: "13.5px",
                width: "150px",
                fontWeight: "600",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => {
                setShowLogs(true);
              }}
            >
              Check Logs
            </Button>
          </Box>
        </Box>
      )}

      {/* Update the InternalAdminForm */}
      <InternalAdminForm
        open={showEditForm}
        onClose={() => {
          setShowEditForm(false);
        }}
        handleSubmit={handleAdminUpdate}
        isEditMode={true}
        existingDetails={{
          name: selectedAdmin?.name || "",
          email: selectedAdmin?.email || "",
          contact: selectedAdmin?.contact || "",
          role: selectedAdmin?.role || "",
          selectedMasjid: selectedAdmin?.masjidDetails?.[0]?._id || "",
          masjidDetails: selectedAdmin?.masjidDetails || [],
        }}
        adminType="external"
        ownershipType={selectedAdmin?.ownershipType}
        delegatedSubAdmin={isDelegatedSubAdmin(selectedAdmin)}
      />
    </div>
  );
};

export default ExternalAdminHandler2;
